"use client"

import { ReactNode, useEffect, useRef } from "react"

interface FixedBlockProps {
  children?: ReactNode
}

const FixedBlock = ({ children }: FixedBlockProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight
        document.documentElement.style.setProperty(
          "--fixed-block-height",
          `${height}px`
        )
      }
    }

    // Update height initially
    updateHeight()

    // Set up a resize observer to update height when container size changes
    const resizeObserver = new ResizeObserver(updateHeight)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    // Set up a mutation observer to detect when children are added/removed via portals
    const mutationObserver = new MutationObserver(updateHeight)
    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      })
    }

    // Update height on window resize as well
    window.addEventListener("resize", updateHeight)

    // Clean up
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current)
        mutationObserver.disconnect()
      }
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateHeight)
    }
  }, []) // No dependency on children since we're using MutationObserver

  return (
    <div className="fixed bottom-0 z-50 w-full bg-coreColors-pageColor">
      {/* Dedicated container for React portals */}
      <div
        id="layout-fixed-block-container"
        className="flex flex-col pb-safe empty:pb-0"
        ref={containerRef}
      >
        {children}
      </div>
    </div>
  )
}

export default FixedBlock
